import { apiRequest } from './core/client';
import type { KeyBinding } from '@/types/keyboard';

// ============================================================================
// Notification Settings
// ============================================================================

export interface NotificationSettings {
  notificationsEnabled: boolean;
}

export async function fetchNotificationSettings(): Promise<NotificationSettings> {
  const data = await apiRequest<unknown>({
    path: '/settings/notifications',
  });
  const record = data as Record<string, unknown>;
  return {
    notificationsEnabled: Boolean(record.notificationsEnabled ?? record.enabled ?? false),
  };
}

export async function updateNotificationSettings(
  payload: { notificationsEnabled: boolean }
): Promise<NotificationSettings> {
  const data = await apiRequest<unknown>({
    path: '/settings/notifications',
    method: 'PUT',
    body: {
      notificationsEnabled: payload.notificationsEnabled,
    },
  });
  const record = data as Record<string, unknown>;
  return {
    notificationsEnabled: Boolean(record.notificationsEnabled ?? false),
  };
}

export interface TestNotificationResult {
  success: boolean;
  reason?: string | null;
}

export async function sendTestNotification(): Promise<TestNotificationResult> {
  const data = await apiRequest<unknown>({
    path: '/settings/notifications/test',
    method: 'POST',
  });
  const record = data as Record<string, unknown>;
  return {
    success: Boolean(record.success),
    reason: typeof record.reason === 'string' ? record.reason : null,
  };
}

// ============================================================================
// Keyboard Shortcut Settings
// ============================================================================

export interface KeyboardShortcutSettings {
  shortcutsEnabled: boolean;
  customBindings: Record<string, KeyBinding | null>;
  disabledShortcuts: string[];
}

export async function updateKeyboardShortcutSettings(
  payload: KeyboardShortcutSettings
): Promise<KeyboardShortcutSettings> {
  const data = await apiRequest<unknown>({
    path: '/settings/keyboard-shortcuts',
    method: 'PUT',
    body: payload,
  });
  const record = data as Record<string, unknown>;
  return {
    shortcutsEnabled: record.shortcutsEnabled !== false,
    customBindings: (record.customBindings as Record<string, KeyBinding | null>) ?? {},
    disabledShortcuts: Array.isArray(record.disabledShortcuts)
      ? record.disabledShortcuts.filter((s): s is string => typeof s === 'string')
      : [],
  };
}

// ============================================================================
// Search Mode Settings
// ============================================================================

export type SearchMode = 'fts' | 'semantic' | 'hybrid';

export interface SearchSettings {
  searchMode: SearchMode;
}

export async function updateSearchModeSettings(searchMode: SearchMode): Promise<SearchSettings> {
  const data = await apiRequest<unknown>({
    path: '/settings/search-mode',
    method: 'PUT',
    body: { searchMode },
  });
  const record = data as Record<string, unknown>;
  const modeValue = typeof record.searchMode === 'string' ? record.searchMode : 'hybrid';
  return {
    searchMode:
      modeValue === 'fts' || modeValue === 'semantic' ? modeValue : 'hybrid',
  };
}

// ============================================================================
// Label Settings
// ============================================================================

export interface LabelSettings {
  labelsVisible: boolean;
}

export async function updateLabelSettings(labelsVisible: boolean): Promise<LabelSettings> {
  const data = await apiRequest<unknown>({
    path: '/settings/labels',
    method: 'PUT',
    body: { labelsVisible },
  });
  const record = data as Record<string, unknown>;
  return {
    labelsVisible: record.labelsVisible !== false,
  };
}

// ============================================================================
// Calendar Settings
// ============================================================================

export interface CalendarSettings {
  defaultCreateCalendarUid: string | null;
}

export async function updateDefaultCalendarSettings(
  defaultCreateCalendarUid: string | null
): Promise<CalendarSettings> {
  const data = await apiRequest<unknown>({
    path: '/settings/default-calendar',
    method: 'PUT',
    body: { defaultCreateCalendarUid },
  });
  const record = data as Record<string, unknown>;
  return {
    defaultCreateCalendarUid:
      typeof record.defaultCreateCalendarUid === 'string'
        ? record.defaultCreateCalendarUid
        : null,
  };
}

// ============================================================================
// App Settings (combined)
// ============================================================================

export interface AppSettings {
  keyboardShortcuts: KeyboardShortcutSettings;
  searchMode: SearchMode;
  defaultCreateCalendarUid: string | null;
  labelsVisible: boolean;
}

export async function fetchAppSettings(): Promise<AppSettings> {
  const data = await apiRequest<unknown>({
    path: '/settings/app',
  });
  const record = data as Record<string, unknown>;

  // Parse keyboard shortcuts
  const kbRecord =
    record.keyboardShortcuts && typeof record.keyboardShortcuts === 'object'
      ? (record.keyboardShortcuts as Record<string, unknown>)
      : {};
  const keyboardShortcuts: KeyboardShortcutSettings = {
    shortcutsEnabled: kbRecord.shortcutsEnabled !== false,
    customBindings: (kbRecord.customBindings as Record<string, KeyBinding | null>) ?? {},
    disabledShortcuts: Array.isArray(kbRecord.disabledShortcuts)
      ? kbRecord.disabledShortcuts.filter((s): s is string => typeof s === 'string')
      : [],
  };

  // Parse search mode
  const searchModeValue = typeof record.searchMode === 'string' ? record.searchMode : 'hybrid';
  const searchMode: SearchMode =
    searchModeValue === 'fts' || searchModeValue === 'semantic'
      ? searchModeValue
      : 'hybrid';

  return {
    keyboardShortcuts,
    searchMode,
    defaultCreateCalendarUid:
      typeof record.defaultCreateCalendarUid === 'string'
        ? record.defaultCreateCalendarUid
        : null,
    labelsVisible: record.labelsVisible !== false,
  };
}
